import React, { useState } from 'react';
import { CDatePicker, CTimePicker } from '@coreui/react-pro';
import './style/style.scss';
import { CalenderIcon } from './assets/images';
import moment from 'moment';

interface ICXDatePickerProps {
  timepicker?: boolean;
  disabled?: boolean;
  zIndex?: number;
  locale?: string;
  onChange: Function;
  placeholder: string;
  value: string;
  onChangeField?: Function;
  field?: string;
  state?: any;
  hasDefault?: boolean;
  additionalValidation?: Function;
  errMsg?: string;
  preserveUtcTime?: boolean;
}

const dateTimeFormat = "YYYY-MM-DDTHH:mm:ss";
const minutePrecisionDatePickerClassName = 'cx-datepicker-minute-precision';
const minutePrecisionTimePickerClassName = 'cx-datepicker-minute-timepicker';

export const getCXDatePickerSelectedDateFormat = (preserveUtcTime = false) =>
  preserveUtcTime ? 'YYYY-MM-DDTHH:mm:ss' : 'YYYY-MM-DDTHH:mm:SS';

export const getCXDatePickerDate = (value?: string, preserveUtcTime = false): string | Date => {
  if (!preserveUtcTime) {
    return !!value ? `${moment(new Date(value)).format(dateTimeFormat)}Z` : `${moment().format(dateTimeFormat)}Z`;
  }

  const utcMoment = value ? moment.utc(value) : moment.utc();

  if (!utcMoment.isValid()) {
    return new Date();
  }

  return new Date(
    utcMoment.year(),
    utcMoment.month(),
    utcMoment.date(),
    utcMoment.hour(),
    utcMoment.minute(),
    utcMoment.second(),
  );
};

const CXDatePicker = ({ disabled = false, timepicker = false, zIndex, locale, onChange, placeholder, value, onChangeField, field, state, hasDefault = true, additionalValidation, errMsg, preserveUtcTime = false }: ICXDatePickerProps) => {
  const [focus, setFocus] = useState(false);
  const [dateTime, setDateTime] = useState<Date | null>(new Date());
  const pickerDate = getCXDatePickerDate(value, preserveUtcTime);
  const useMinutePrecisionTimepicker = preserveUtcTime && timepicker;
  const datePickerClassName = `cx-datepicker-body ${useMinutePrecisionTimepicker ? minutePrecisionDatePickerClassName : ''} ${additionalValidation && !additionalValidation(value) ? "error-border" : ""} ${disabled ? "cx-disabled" : ""}`;
  /*
    ----------------------------- Sample component ------------------------------
    const [displayDate, setDisplayDate] = useState([pass in UTC date]);
    <CXDatePicker placeholder={"STD"} value={displayDate} onChange={setDisplayDate} timepicker={true} />
    ----------------------------- Sample component ------------------------------
  */

  /* 
    ----------------------------- Save for future debugging -----------------------------
    console.log(` 1. first enter ${value} `);
    console.log(` 2. CDatePicker date ${!!value ? `${moment.utc(new Date(value)).format("YYYY-MM-DDTHH:mm:ss")}Z` : `${moment.utc().format("YYYY-MM-DDTHH:mm:ss")}Z`} `);
    ----------------------------- Save for future debugging -----------------------------
  */

  const toggleFocus = () => {
    if (!disabled) {
      setFocus(!focus);
    }
  };

  const getCurrentPickerDate = () => {
    if (pickerDate instanceof Date) {
      return pickerDate;
    }

    return new Date(pickerDate);
  };

  const mergeDateAndTime = (datePart?: Date | null, timePart?: Date | null) => {
    const fallbackMoment = moment(getCurrentPickerDate());
    const dateMoment = datePart ? moment(datePart) : fallbackMoment;
    const timeMoment = timePart ? moment(timePart) : fallbackMoment;

    return dateMoment
      .hour(timeMoment.hour())
      .minute(timeMoment.minute())
      .second(0)
      .millisecond(0)
      .toDate();
  };

  // selectedDate would be set to the time you selected 
  // but be changed into the timezone of your device
  const select = (selectedDate) => {
    // return utc time to onChange function
    // frankie version
    // ${moment.utc(new Date(selectedDate)).add(8, 'hours').format("YYYY-MM-DDTHH:mm:ss")}
    const selectedMoment = moment(selectedDate);
    const dateStr = (preserveUtcTime ? selectedMoment.second(0) : selectedMoment).format(getCXDatePickerSelectedDateFormat(preserveUtcTime));
    if (onChangeField) {
      onChangeField(`${dateStr}Z`, field, state, onChange);
    } else {
      onChange(`${dateStr}Z`);
    }
    setFocus(false);
  };

  const selectDate = (selectedDate) => {
    if (useMinutePrecisionTimepicker) {
      select(mergeDateAndTime(selectedDate, getCurrentPickerDate()));
      return;
    }

    select(selectedDate);
  };

  const selectTime = (selectedTime?: Date) => {
    if (!selectedTime) {
      return;
    }

    select(mergeDateAndTime(getCurrentPickerDate(), selectedTime));
  };

  const minutePrecisionTimepicker = useMinutePrecisionTimepicker ? (
    <div className={minutePrecisionTimePickerClassName}>
      <CTimePicker
        container={"inline"}
        disabled={disabled}
        footer={false}
        indicator={false}
        locale={locale}
        onTimeChange={(_, __, selectedTime) => selectTime(selectedTime)}
        seconds={false}
        time={getCurrentPickerDate()}
        variant={"select"}
      />
    </div>
  ) : undefined;

  return (
    <>
      <div className={"cx-datepicker"} style={{ zIndex: !!zIndex ? zIndex : 1038 }}>
        <div className={"cx-form-control"}>
          {hasDefault ?
            <CDatePicker
              className={datePickerClassName}
              date={pickerDate}
              indicator={false}
              locale={locale}
              placeholder={""}
              onDateChange={(selectedDate) => selectDate(selectedDate)}
              timepicker={useMinutePrecisionTimepicker ? false : timepicker}
              disabled={disabled}
              {...(useMinutePrecisionTimepicker ? {
                footer: true,
                footerContent: minutePrecisionTimepicker,
                todayButton: false,
              } : {})}
            />
            :
            <CDatePicker
              className={datePickerClassName}
              indicator={false}
              locale={locale}
              onDateChange={(selectedDate) => { setDateTime(selectedDate) }}
              onConfirm={() => { select(dateTime) }}
              timepicker={useMinutePrecisionTimepicker ? false : timepicker}
              footer
              disabled={disabled}
              {...(useMinutePrecisionTimepicker ? {
                footerContent: minutePrecisionTimepicker,
                todayButton: false,
              } : {})}
            />
          }
          <CalenderIcon className={`cx-calender-icon dropdown-toggle ${disabled ? "cx-disabled" : ""}`} height={16} width={16} />
          {(focus || !!value) && <div className={"focused-label cx-datepicker-labels"} onClick={toggleFocus}>{placeholder}</div>}
          {(focus || !!value) && <div className={"selected-date cx-datepicker-labels"}>{!!value && !disabled ? moment.utc(new Date(value)).format(timepicker ? "DDMMMYYYY,HH:mm" : "DDMMMYYYY") : ""}</div>}
          {(!focus && !value) && <div className={`not-focused-label cx-datepicker-labels ${disabled ? "cx-disabled" : ""}`} onClick={toggleFocus}>{placeholder}</div>}
        </div>
      </div>
      {additionalValidation && !additionalValidation(value) && !!errMsg && (
        <div className={"cx-error-message"}>{errMsg}</div>
      )}
    </>
  );
};

export default CXDatePicker;
